import { devConfig } from "./appConfig.js";
import { applyDevConfig } from "./applyDevConfig.js";
import {
  applyArCautionList,
  applySplashChecklist,
  uiLexicon,
} from "./uiLexicon.js";
import {
  initAudioFx,
  playBootLoadingOpenFx,
  playBootPhaseTickFx,
  playBootReadyFx,
  playBootStepOkFx,
  playDetectFx,
  playErrorFx,
  playLockFx,
} from "./audioFx.js";
import * as facePrivacy from "./facePrivacy.js";
import { formatTargetDistanceHudFragment } from "./objectRange.js";
import {
  resetLiveHealth,
  startVideoStallWatch,
  stopVideoStallWatch,
  subscribeLiveHealth,
} from "./liveHealth.js";
import {
  stopWebXrHitTestRangeSensor,
  tryStartWebXrHitTestRangeSensor,
} from "./webxrHitTestRange.js";

applyDevConfig();

const bootOverlay = document.getElementById("bootOverlay");
const bootTitle = document.getElementById("bootTitle");
const bootStatus = document.getElementById("bootStatus");
const bootProgress = document.getElementById("bootProgress");
const bootStepSensorsLabel = document.getElementById("bootStepSensorsLabel");
const bootStepSensorsState = document.getElementById("bootStepSensorsState");
const bootStepModelLabel = document.getElementById("bootStepModelLabel");
const bootStepModelState = document.getElementById("bootStepModelState");
const bootStepCameraLabel = document.getElementById("bootStepCameraLabel");
const bootStepCameraState = document.getElementById("bootStepCameraState");
const video = document.getElementById("camera");
const bboxOverlay = document.getElementById("bboxOverlay");
const facePrivacyOverlay = document.getElementById("facePrivacyOverlay");
const miniMapPanel = document.getElementById("miniMapPanel");
const miniMapMount = document.getElementById("miniMapMount");
const miniMapStatus = document.getElementById("miniMapStatus");
const speedHud = document.getElementById("speedHud");
const speedHudValue = document.getElementById("speedHudValue");
const liveHud = document.getElementById("liveHud");
const liveHudTitle = document.querySelector(".live-hud__title");
const miniMapTitle = document.querySelector(".mini-map-panel__title");
const reticleLayer = document.getElementById("reticleLayer");
const crtOverlay = document.getElementById("crtOverlay");
const noiseCanvas = document.getElementById("crtNoise");
const biosOverlay = document.getElementById("biosOverlay");
const splash = document.getElementById("splash");
const startBtn = document.getElementById("startBtn");
const stopBtn = document.getElementById("stopBtn");
const statusEl = document.getElementById("status");
const compassPanel = document.getElementById("compassPanel");
const compassRose = document.getElementById("compassRose");
const compassTapeWindow = document.getElementById("compassTapeWindow");
const compassHint = document.getElementById("compassHint");
const altitudeTapeColumn = document.getElementById("altitudeTapeColumn");
const relativeTapeRow = document.getElementById("relativeTapeRow");
const relativeZeroHud = document.getElementById("relativeZeroHud");
const relativeHeadingRose = document.getElementById("relativeHeadingRose");
const relativeTapeWindow = document.getElementById("relativeTapeWindow");
const relativeHeadingDeg = document.getElementById("relativeHeadingDeg");
const altitudeTapeSvg = document.getElementById("altitudeTapeSvg");
const altitudeTapeWindow = document.getElementById("altitudeTapeWindow");
const altitudeReadout = document.getElementById("altitudeReadout");
const motionFeedback = document.getElementById("motionFeedback");
const targetFeedback = document.getElementById("targetFeedback");
const splashBrand = document.getElementById("splashBrand");
const splashLead = document.getElementById("splashLead");
const splashManifest = document.getElementById("splashManifest");

function applyStaticShellLabels() {
  document.title = uiLexicon.documentTitle;
  const metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) metaDesc.setAttribute("content", uiLexicon.metaDescription);
  const seqLabel = document.querySelector(".splash__seq-banner-label");
  const seqSub = document.querySelector(".splash__seq-banner-sub");
  if (seqLabel) seqLabel.textContent = uiLexicon.seqBanner.label;
  if (seqSub) seqSub.textContent = uiLexicon.seqBanner.sub;
  applySplashChecklist();
  applyArCautionList();
  const arTitle = document.querySelector(".splash__ar-caution-title");
  if (arTitle) arTitle.textContent = uiLexicon.splash.arTitle;
  const policySum = document.querySelector(".policy summary");
  if (policySum) policySum.textContent = uiLexicon.splash.policySummary;
  const policyText = document.querySelector(".policy__text");
  if (policyText) policyText.textContent = uiLexicon.splash.policyNote;
  const policyLinks = document.querySelectorAll(".policy__links a");
  if (policyLinks[0]) policyLinks[0].textContent = uiLexicon.splash.policyPrivacyLink;
  if (policyLinks[1]) policyLinks[1].textContent = uiLexicon.splash.policyOssLink;
  if (video) video.setAttribute("aria-label", uiLexicon.aria.video);
  if (stopBtn) {
    stopBtn.textContent = uiLexicon.stopFab;
    stopBtn.setAttribute("aria-label", uiLexicon.aria.stopCamera);
  }
  if (compassPanel) compassPanel.setAttribute("aria-label", uiLexicon.aria.compass);
  if (miniMapPanel) miniMapPanel.setAttribute("aria-label", uiLexicon.aria.miniMap);
  const bootSteps = document.querySelector(".boot-overlay__steps");
  if (bootSteps) bootSteps.setAttribute("aria-label", uiLexicon.aria.bootSteps);
  const splashChecks = document.querySelector(".splash__checks");
  if (splashChecks) splashChecks.setAttribute("aria-label", uiLexicon.aria.checks);
  const arList = document.querySelector(".splash__ar-caution-list");
  if (arList) arList.setAttribute("aria-label", uiLexicon.aria.arCaution);
  if (startBtn) startBtn.textContent = uiLexicon.splash.startBtn;
  if (splashBrand) splashBrand.textContent = uiLexicon.splash.brand;
  if (splashLead) splashLead.textContent = uiLexicon.splash.lead;
  if (splashManifest) splashManifest.textContent = uiLexicon.splash.manifest;
  const speedHudLabel = document.getElementById("speedHudLabel");
  const speedHudUnit = document.getElementById("speedHudUnit");
  if (speedHudLabel) speedHudLabel.textContent = uiLexicon.speedHud.label;
  if (speedHudUnit) speedHudUnit.textContent = uiLexicon.speedHud.unit;
}

applyStaticShellLabels();

/** @type {Promise<typeof import("./personDetection.js")> | null} */
let personDetectionApiPromise = null;
/** @type {Promise<typeof import("./miniMap.js")> | null} */
let miniMapApiPromise = null;
/** @type {Promise<typeof import("./compass.js")> | null} */
let compassApiPromise = null;
/** @type {Promise<typeof import("./motionHud.js")> | null} */
let motionHudApiPromise = null;
/** @type {Promise<typeof import("./motionStabilityLink.js")> | null} */
let motionStabilityApiPromise = null;

function loadPersonDetectionApi() {
  personDetectionApiPromise ??= import("./personDetection.js");
  return personDetectionApiPromise;
}

function loadMiniMapApi() {
  miniMapApiPromise ??= import("./miniMap.js");
  return miniMapApiPromise;
}

function loadCompassApi() {
  compassApiPromise ??= import("./compass.js");
  return compassApiPromise;
}

function loadMotionHudApi() {
  motionHudApiPromise ??= import("./motionHud.js");
  return motionHudApiPromise;
}

function loadMotionStabilityApi() {
  motionStabilityApiPromise ??= import("./motionStabilityLink.js");
  return motionStabilityApiPromise;
}

function setMiniMapHeadingSafe(headingDeg) {
  if (!miniMapApiPromise) return;
  void miniMapApiPromise.then((api) => api.setMiniMapHeading(headingDeg));
}

/** @type {number} */
let targetFeedbackTimer = 0;
/** @type {number[]} */
let splashScrambleTimers = [];
/** @type {number[]} */
let biosScrambleTimers = [];

/** @type {number} */
let bootHideTimer = 0;
/** @type {number} */
let bootTrickleTimer = 0;
/** @type {number} */
let bootProgressValue = 0;
/** @type {number[]} */
let bootScrambleTimers = [];
/** @type {Map<HTMLElement, number>} */
const bootScrambleTokenByEl = new Map();

/**
 * スペース・中黒・句点など：ノイズにせず常に確定表示する
 * @param {string} c
 */
function isScrambleStaticChar(c) {
  return c === " " || c === "・" || c === "·" || c === ".";
}

/**
 * @param {string} text
 * @param {{ level?: "detected" | "lock"; holdMs?: number }} [opts]
 */
function playScrambleText(text, opts = {}) {
  if (!targetFeedback) return;
  const { level = "detected", holdMs = 900 } = opts;
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<>+-/*";
  const finalText = text.toUpperCase();
  let frame = 0;
  const totalFrames = Math.max(8, finalText.length * 2);
  targetFeedback.hidden = false;
  targetFeedback.dataset.level = level === "lock" ? "lock" : "";
  clearTimeout(targetFeedbackTimer);

  const tick = () => {
    frame += 1;
    const reveal = Math.floor((frame / totalFrames) * finalText.length);
    let out = "";
    for (let i = 0; i < finalText.length; i += 1) {
      if (i < reveal || isScrambleStaticChar(finalText[i])) {
        out += finalText[i];
      } else {
        out += chars[(Math.random() * chars.length) | 0];
      }
    }
    targetFeedback.textContent = out;
    if (frame < totalFrames) {
      window.setTimeout(tick, 26);
      return;
    }
    targetFeedback.textContent = finalText;
    targetFeedbackTimer = window.setTimeout(() => {
      targetFeedback.hidden = true;
      targetFeedback.textContent = "";
    }, holdMs);
  };
  tick();
}

/**
 * @param {HTMLElement | null} el
 * @param {string} finalText
 * @param {{ delayMs?: number; stepMs?: number; onDone?: () => void }} [opts]
 */
function scrambleElementText(el, finalText, opts = {}) {
  if (!el) return;
  const { delayMs = 0, stepMs = 24 } = opts;
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<>+-/*";
  let frame = 0;
  const totalFrames = Math.max(10, finalText.length * 2);

  const startTimer = window.setTimeout(() => {
    const tick = () => {
      frame += 1;
      const reveal = Math.floor((frame / totalFrames) * finalText.length);
      let out = "";
      for (let i = 0; i < finalText.length; i += 1) {
        if (i < reveal || isScrambleStaticChar(finalText[i])) {
          out += finalText[i];
        } else {
          out += chars[(Math.random() * chars.length) | 0];
        }
      }
      el.textContent = out;
      if (frame < totalFrames) {
        const loopTimer = window.setTimeout(tick, stepMs);
        splashScrambleTimers.push(loopTimer);
        return;
      }
      el.textContent = finalText;
      opts.onDone?.();
    };
    tick();
  }, delayMs);
  splashScrambleTimers.push(startTimer);
}

function clearBiosScrambleTimers() {
  for (const t of biosScrambleTimers) clearTimeout(t);
  biosScrambleTimers = [];
}

/**
 * BIOS 用スクランブル（スプラッシュ用タイマーと分離）
 * @param {HTMLElement | null} el
 * @param {string} finalText
 * @param {{ delayMs?: number; stepMs?: number; onDone?: () => void }} [opts]
 */
function scrambleBiosText(el, finalText, opts = {}) {
  if (!el) return;
  const { delayMs = 0, stepMs = 20, onDone } = opts;
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<>+-/*";
  let frame = 0;
  const totalFrames = Math.max(10, finalText.length * 2);

  const startTimer = window.setTimeout(() => {
    const tick = () => {
      frame += 1;
      const reveal = Math.floor((frame / totalFrames) * finalText.length);
      let out = "";
      for (let i = 0; i < finalText.length; i += 1) {
        if (i < reveal || isScrambleStaticChar(finalText[i])) {
          out += finalText[i];
        } else {
          out += chars[(Math.random() * chars.length) | 0];
        }
      }
      el.textContent = out;
      if (frame < totalFrames) {
        const loopTimer = window.setTimeout(tick, stepMs);
        biosScrambleTimers.push(loopTimer);
        return;
      }
      el.textContent = finalText;
      onDone?.();
    };
    tick();
  }, delayMs);
  biosScrambleTimers.push(startTimer);
}

/**
 * ステータス列：中間表示から確定へスクランブルで切り替え
 * @param {HTMLElement | null} el
 * @param {string} interim
 * @param {string} finalText
 * @param {{ delayMs?: number; gapMs?: number; stepMs?: number }} [opts]
 */
function biosStatFlip(el, interim, finalText, opts = {}) {
  if (!el) return;
  const { delayMs = 0, gapMs = 220, stepMs = 15 } = opts;
  scrambleBiosText(el, interim, {
    delayMs,
    stepMs,
    onDone: () => {
      const t = window.setTimeout(() => {
        scrambleBiosText(el, finalText, { delayMs: 0, stepMs });
      }, gapMs);
      biosScrambleTimers.push(t);
    },
  });
}

function clearSplashScrambleTimers() {
  for (const t of splashScrambleTimers) clearTimeout(t);
  splashScrambleTimers = [];
}

function clearBootScrambleTimers() {
  for (const t of bootScrambleTimers) clearTimeout(t);
  bootScrambleTimers = [];
  bootScrambleTokenByEl.clear();
}

/**
 * @param {HTMLElement | null} el
 * @param {string} finalText
 * @param {{ stepMs?: number }} [opts]
 */
function scrambleBootText(el, finalText, opts = {}) {
  if (!el) return;
  const { stepMs = 18 } = opts;
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<>+-/*";
  const src = finalText.toUpperCase();
  let frame = 0;
  const totalFrames = Math.max(8, src.length * 2);
  const token = (bootScrambleTokenByEl.get(el) ?? 0) + 1;
  bootScrambleTokenByEl.set(el, token);

  const tick = () => {
    if (bootScrambleTokenByEl.get(el) !== token) return;
    frame += 1;
    const reveal = Math.floor((frame / totalFrames) * src.length);
    let out = "";
    for (let i = 0; i < src.length; i += 1) {
      if (i < reveal || isScrambleStaticChar(src[i])) {
        out += src[i];
      } else {
        out += chars[(Math.random() * chars.length) | 0];
      }
    }
    el.textContent = out;
    if (frame < totalFrames) {
      const timer = window.setTimeout(tick, stepMs);
      bootScrambleTimers.push(timer);
      return;
    }
    el.textContent = src;
  };
  tick();
}

/**
 * @param {{ alignWithSeqLayer?: boolean }} [opts]
 * INIT SEQUENCE の段階表示と齟齬が出ないよう、CSS の animation-delay に合わせる。
 */
function playSplashIntroText(opts = {}) {
  clearSplashScrambleTimers();
  const { alignWithSeqLayer = false } = opts;
  const brandDelay = alignWithSeqLayer ? 100 : 40;
  const leadDelay = alignWithSeqLayer ? 200 : 210;
  const manifestDelay = alignWithSeqLayer ? 300 : 380;
  scrambleElementText(splashBrand, uiLexicon.splash.brand, {
    delayMs: brandDelay,
    stepMs: 22,
  });
  scrambleElementText(
    splashLead,
    uiLexicon.splash.lead,
    { delayMs: leadDelay, stepMs: 18 },
  );
  scrambleElementText(
    splashManifest,
    uiLexicon.splash.manifest,
    { delayMs: manifestDelay, stepMs: 16 },
  );
}

/**
 * @param {{ title?: string; status?: string; progress?: number; visible?: boolean }} patch
 */
function updateBootOverlay(patch) {
  if (!bootOverlay || !bootTitle || !bootStatus || !bootProgress) return;
  if (typeof patch.visible === "boolean") {
    bootOverlay.hidden = !patch.visible;
  }
  if (typeof patch.title === "string") scrambleBootText(bootTitle, patch.title, { stepMs: 16 });
  if (typeof patch.status === "string") scrambleBootText(bootStatus, patch.status, { stepMs: 14 });
  if (typeof patch.progress === "number") {
    const p = Math.max(0, Math.min(100, patch.progress));
    bootProgressValue = p;
    bootProgress.style.width = `${p}%`;
  }
}

/**
 * @param {"sensors" | "model" | "camera"} key
 * @param {"pending" | "loading" | "ok" | "error"} state
 */
function updateBootStep(key, state) {
  const table = {
    sensors: { label: bootStepSensorsLabel, value: bootStepSensorsState },
    model: { label: bootStepModelLabel, value: bootStepModelState },
    camera: { label: bootStepCameraLabel, value: bootStepCameraState },
  };
  const row = table[key];
  if (!row?.value) return;
  const textMap = {
    pending: "PENDING",
    loading: "LOADING",
    ok: "OK",
    error: "ERROR",
  };
  scrambleBootText(row.label, key.toUpperCase(), { stepMs: 20 });
  scrambleBootText(row.value, textMap[state], { stepMs: 14 });
  row.value.dataset.state = state;
  const colorMap = {
    pending: "var(--ui-green-dim)",
    loading: "var(--hud-warn)",
    ok: "var(--ui-green-bright)",
    error: "var(--danger)",
  };
  row.value.style.color = colorMap[state];
}

/**
 * @param {number} target
 * @param {number} durationMs
 */
function animateBootProgressTo(target, durationMs) {
  const t = Math.max(0, Math.min(100, target));
  const d = Math.max(60, durationMs);
  const start = bootProgressValue;
  if (Math.abs(t - start) < 0.5) {
    updateBootOverlay({ progress: t });
    return Promise.resolve();
  }
  const t0 = performance.now();
  return new Promise((resolve) => {
    const step = () => {
      const now = performance.now();
      const k = Math.min(1, (now - t0) / d);
      const eased = 1 - (1 - k) * (1 - k);
      updateBootOverlay({ progress: start + (t - start) * eased });
      if (k >= 1) {
        resolve();
        return;
      }
      window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
  });
}

/**
 * @param {number} cap
 */
function startBootTrickle(cap) {
  clearTimeout(bootTrickleTimer);
  const max = Math.max(0, Math.min(99, cap));
  const loop = () => {
    const remaining = max - bootProgressValue;
    if (remaining <= 0.4) return;
    const step = Math.max(0.35, Math.min(2.2, remaining * 0.16));
    updateBootOverlay({ progress: bootProgressValue + step });
    const wait = Math.max(70, Math.min(220, 190 - remaining * 2.5));
    bootTrickleTimer = window.setTimeout(loop, wait);
  };
  bootTrickleTimer = window.setTimeout(loop, 90);
}

function stopBootTrickle() {
  clearTimeout(bootTrickleTimer);
  bootTrickleTimer = 0;
}

function playLiveHudIntroText() {
  clearSplashScrambleTimers();
  scrambleElementText(liveHudTitle, uiLexicon.liveHud.title, {
    delayMs: 30,
    stepMs: 18,
  });
  scrambleElementText(miniMapTitle, uiLexicon.liveHud.mapTitle, {
    delayMs: 120,
    stepMs: 16,
  });
}

function isLikelyMobileDevice() {
  const uaDataMobile = navigator.userAgentData?.mobile;
  if (typeof uaDataMobile === "boolean") return uaDataMobile;
  const ua = navigator.userAgent || "";
  const byUa =
    /Android|iPhone|iPad|iPod|Mobile|Windows Phone|webOS/i.test(ua);
  const byTouch = navigator.maxTouchPoints > 1;
  const byCoarse = window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
  return byUa || (byTouch && byCoarse);
}

/**
 * デバイスの向きセンサが「使える想定」か（タブレット・タッチ PC 等も含む）
 */
function deviceLikelyHasOrientationSensor() {
  if (isLikelyMobileDevice()) return true;
  const touch =
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 0;
  const coarse = window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
  return touch && coarse;
}

function deviceOrientationApiAvailable() {
  return typeof window.DeviceOrientationEvent !== "undefined";
}

/**
 * 方位・傾きテープ HUD を出すか（API 有無・端末・開発用プレビュー）
 */
function orientationHudEnabled() {
  if (!deviceOrientationApiAvailable()) return false;
  if (devConfig.ui?.previewHudOnDesktop === true) return true;
  return deviceLikelyHasOrientationSensor();
}

/** 傾きテープ用：直近の β（テープ位置・リサイズ再適用） */
/** @type {number | null} */
let lastTiltTapeBeta = null;
/** @type {number | null} */
let lastTiltTapeGamma = null;

/** @type {MediaStream | null} */
let stream = null;
/** @type {(() => void) | null} */
let compassCleanup = null;
/** @type {(() => void) | null} */
let motionHudCleanup = null;
/** @type {(() => void) | null} */
let motionStabilityCleanup = null;
/** @type {number} */
let lastHeading = 0;
/**
 * 方位テープ用の連続角（°）。センサは [0,360) に正規化されるが、N 跨ぎでは前値に ±360
 * を足してテープの translate が飛ばないようにする。
 * @type {number | null}
 */
let compassTapeDisplayDeg = null;

/** 高度テープ用（リサイズ時に再適用） */
/** @type {number | null} */
let lastAltitudeM = null;

/** @param {number} delta */
function normalizeAngleDelta180(delta) {
  let d = delta;
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  return d;
}

/** @returns {number} */
function getCompassTapeCycles() {
  if (!compassRose) return 5;
  const rawCycles = parseInt(
    String(compassRose.dataset.tapeCycles ?? "5"),
    10,
  );
  return Number.isFinite(rawCycles) && rawCycles >= 1 && rawCycles <= 12
    ? rawCycles
    : 5;
}

/**
 * 連続角を中央周付近に寄せ、テープ端までスクロールして N が切れるのを防ぐ。
 * ±360° は見た目が同じなのでジャンプしない。
 */
function recenterCompassTapeDisplayDeg() {
  if (compassTapeDisplayDeg == null) return;
  const cycles = getCompassTapeCycles();
  const mid = Math.floor(cycles / 2) * 360;
  const min = mid - 180;
  const max = mid + 180;
  while (compassTapeDisplayDeg < min) compassTapeDisplayDeg += 360;
  while (compassTapeDisplayDeg > max) compassTapeDisplayDeg -= 360;
}

/**
 * テープの幾何だけ再計算（リサイズ・レイアウト後）。連続角が無いときは lastHeading を使う。
 */
function syncCompassTapeGeometry() {
  if (!compassRose || !compassTapeWindow) return;
  const tapeW = compassRose.getBoundingClientRect().width;
  if (tapeW <= 0) return;
  const tapeCycles = getCompassTapeCycles();
  const pxPerDeg = tapeW / (360 * tapeCycles);
  const cx = compassTapeWindow.getBoundingClientRect().width / 2;
  const tapeInset =
    compassRose instanceof HTMLElement ? compassRose.offsetLeft : 0;
  const deg = compassTapeDisplayDeg ?? lastHeading;
  compassRose.style.transform = `translateX(${cx - tapeInset - deg * pxPerDeg}px)`;
}

/**
 * コンパスからの方位（0…360）でテープを更新。N 境界は連続角で吸収。
 * @param {number} headingDeg
 */
function feedCompassTapeHeading(headingDeg) {
  const norm = ((headingDeg % 360) + 360) % 360;
  lastHeading = norm;
  if (compassTapeDisplayDeg == null) {
    compassTapeDisplayDeg = norm;
  } else {
    const prevNorm = ((compassTapeDisplayDeg % 360) + 360) % 360;
    let d = norm - prevNorm;
    d = normalizeAngleDelta180(d);
    compassTapeDisplayDeg += d;
  }
  recenterCompassTapeDisplayDeg();
  syncCompassTapeGeometry();
}

function resetCompassTapeUnwrap() {
  compassTapeDisplayDeg = null;
}

/**
 * 傾きテープ（DeviceOrientation β を縦スクロール、-180…180° をテープ高にマッピング）
 * @param {number | null} betaDeg
 */
function applyTiltTapeTransform(betaDeg) {
  lastTiltTapeBeta = betaDeg;
  if (!relativeHeadingRose || !relativeTapeWindow) return;
  const tapeH = relativeHeadingRose.getBoundingClientRect().height;
  if (tapeH <= 0) return;
  const cy = relativeTapeWindow.getBoundingClientRect().height / 2;
  if (betaDeg == null || Number.isNaN(betaDeg)) {
    relativeHeadingRose.style.transform = "translateY(0px)";
    if (relativeHeadingDeg) {
      const g = lastTiltTapeGamma;
      const gLine =
        g == null || Number.isNaN(g) ? "γ --°" : `γ ${g >= 0 ? "+" : ""}${g.toFixed(1)}°`;
      relativeHeadingDeg.textContent = `β --°\n${gLine}`;
    }
    return;
  }
  const b = Math.max(-180, Math.min(180, betaDeg));
  const yTarget = ((b + 180) / 360) * tapeH;
  relativeHeadingRose.style.transform = `translateY(${cy - yTarget}px)`;
  if (relativeHeadingDeg) {
    const g = lastTiltTapeGamma;
    const gLine =
      g == null || Number.isNaN(g) ? "γ --°" : `γ ${g >= 0 ? "+" : ""}${g.toFixed(1)}°`;
    relativeHeadingDeg.textContent = `β ${b >= 0 ? "+" : ""}${b.toFixed(1)}°\n${gLine}`;
  }
}

function resetTiltTapeHud() {
  lastTiltTapeBeta = null;
  lastTiltTapeGamma = null;
  if (relativeHeadingRose) relativeHeadingRose.style.transform = "translateY(0px)";
  if (relativeHeadingDeg) relativeHeadingDeg.textContent = "β --°\nγ --°";
}

/**
 * deviceorientation からの傾き（β / γ）で右縦テープを更新
 * @param {{ beta: number | null; gamma: number | null }} d
 */
function updateTiltTape(d) {
  const beta = d.beta;
  const gamma = d.gamma;
  if (gamma != null && !Number.isNaN(gamma)) lastTiltTapeGamma = gamma;
  applyTiltTapeTransform(beta);
}

/**
 * 高度テープ（縦・360 m を 1 周とする目盛り、値は GPS 楕円体高の絶対値 m）
 * @param {number | null} altitudeM
 */
function applyAltitudeTapeTransform(altitudeM) {
  lastAltitudeM = altitudeM;
  if (!altitudeTapeSvg || !altitudeTapeWindow) return;
  const tapeH = altitudeTapeSvg.getBoundingClientRect().height;
  if (tapeH <= 0) return;
  const cy = altitudeTapeWindow.getBoundingClientRect().height / 2;
  if (altitudeM == null || Number.isNaN(altitudeM)) {
    altitudeTapeSvg.style.transform = "translateY(0px)";
    if (altitudeReadout) altitudeReadout.textContent = "-- m";
    return;
  }
  const mod = ((altitudeM % 360) + 360) % 360;
  const yTarget = (mod / 360) * tapeH;
  altitudeTapeSvg.style.transform = `translateY(${cy - yTarget}px)`;
  if (altitudeReadout) {
    altitudeReadout.textContent = `${Math.round(altitudeM)} m`;
  }
}

function resetAltitudeHud() {
  lastAltitudeM = null;
  if (altitudeTapeSvg) altitudeTapeSvg.style.transform = "translateY(0px)";
  if (altitudeReadout) altitudeReadout.textContent = "-- m";
}

/** @type {number} */
let noiseRaf = 0;
/** @type {number} */
let liveCrtBurstEndTimer = 0;
/** @type {number} */
let liveCrtBurstNextTimer = 0;

/** @type {CanvasRenderingContext2D | null} */
let noiseCtx = null;

function setStatus(message, isError = false) {
  statusEl.textContent = message;
  statusEl.classList.toggle("error", isError);
}

/**
 * @template T
 * @param {Promise<T>} promise
 * @param {number} timeoutMs
 * @param {string} message
 * @returns {Promise<T>}
 */
function withTimeout(promise, timeoutMs, message) {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(message));
    }, timeoutMs);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/** @type {(() => void) | null} */
let liveHealthUnsubscribe = null;

function ensureLiveHealthUi() {
  if (liveHealthUnsubscribe) return;
  liveHealthUnsubscribe = subscribeLiveHealth(({ kind, code, message }) => {
    if (kind === "alert") {
      setStatus(message, true);
      if (code === "inferFail" || code === "videoStall") {
        playErrorFx();
      }
      return;
    }
    if (stream && !message) {
      setStatus("");
    }
  });
}

function resizeNoiseCanvas() {
  if (!noiseCanvas) return;
  const cell = devConfig.crt.canvasNoise.cellPx;
  const w = Math.max(1, Math.ceil(window.innerWidth / cell));
  const h = Math.max(1, Math.ceil(window.innerHeight / cell));
  noiseCanvas.width = w;
  noiseCanvas.height = h;
}

function drawNoiseFrame() {
  if (!noiseCtx || !noiseCanvas) return;
  const { alphaMin, alphaMax } = devConfig.crt.canvasNoise;
  const aSpan = Math.max(0, alphaMax - alphaMin);
  const w = noiseCanvas.width;
  const h = noiseCanvas.height;
  const img = noiseCtx.createImageData(w, h);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const v = Math.random() * 255;
    d[i] = v;
    d[i + 1] = v;
    d[i + 2] = v;
    d[i + 3] = Math.floor(Math.random() * aSpan + alphaMin);
  }
  noiseCtx.putImageData(img, 0, 0);
}

function noiseLoop() {
  if (!stream) return;
  drawNoiseFrame();
  noiseRaf = requestAnimationFrame(noiseLoop);
}

function stopNoiseLoop() {
  if (noiseRaf) {
    cancelAnimationFrame(noiseRaf);
    noiseRaf = 0;
  }
}

function startCrtNoise() {
  if (!noiseCanvas) return;
  noiseCtx ??= noiseCanvas.getContext("2d", { alpha: true });
  if (!noiseCtx) return;
  resizeNoiseCanvas();
  stopNoiseLoop();
  noiseRaf = requestAnimationFrame(noiseLoop);
}

const LIVE_CRT_BURST_MS = 280;
const LIVE_CRT_BURST_GAP_MIN = 3600;
const LIVE_CRT_BURST_GAP_RANGE = 6400;

function clearLiveCrtBurstTimers() {
  if (liveCrtBurstEndTimer) {
    clearTimeout(liveCrtBurstEndTimer);
    liveCrtBurstEndTimer = 0;
  }
  if (liveCrtBurstNextTimer) {
    clearTimeout(liveCrtBurstNextTimer);
    liveCrtBurstNextTimer = 0;
  }
  if (crtOverlay) {
    crtOverlay.classList.remove(
      "crt-overlay--shake-burst",
      "crt-overlay--noise-surge",
      "crt-overlay--glitch-burst",
    );
  }
  video.classList.remove("camera--burst-warp");
  if (facePrivacyOverlay && devConfig.facePrivacy.enabled) {
    facePrivacyOverlay.classList.remove("camera--burst-warp");
  }
}

/**
 * ライブ中：CRT 全体を短く揺らし、ノイズ層を一瞬強める（ランダム間隔で繰り返し）
 */
function armLiveCrtBurstLoop() {
  clearLiveCrtBurstTimers();
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const tick = () => {
    if (!stream || !crtOverlay || crtOverlay.hidden) return;
    crtOverlay.classList.add(
      "crt-overlay--shake-burst",
      "crt-overlay--noise-surge",
      "crt-overlay--glitch-burst",
    );
    video.classList.add("camera--burst-warp");
    if (facePrivacyOverlay && devConfig.facePrivacy.enabled) {
      facePrivacyOverlay.classList.add("camera--burst-warp");
    }
    liveCrtBurstEndTimer = window.setTimeout(() => {
      liveCrtBurstEndTimer = 0;
      crtOverlay?.classList.remove(
        "crt-overlay--shake-burst",
        "crt-overlay--noise-surge",
        "crt-overlay--glitch-burst",
      );
      video.classList.remove("camera--burst-warp");
      if (facePrivacyOverlay && devConfig.facePrivacy.enabled) {
        facePrivacyOverlay.classList.remove("camera--burst-warp");
      }
      if (!stream || !crtOverlay || crtOverlay.hidden) return;
      const gap = LIVE_CRT_BURST_GAP_MIN + Math.random() * LIVE_CRT_BURST_GAP_RANGE;
      liveCrtBurstNextTimer = window.setTimeout(tick, gap);
    }, LIVE_CRT_BURST_MS);
  };

  const firstGap = 2200 + Math.random() * 2800;
  liveCrtBurstNextTimer = window.setTimeout(tick, firstGap);
}

/**
 * @param {boolean} active
 * @param {{ orientationGranted?: boolean; enableMobileSensors?: boolean }} [opts]
 */
async function setLive(active, opts = {}) {
  const { orientationGranted = true, enableMobileSensors = true } = opts;
  const showHudChrome = enableMobileSensors;
  if (active) {
    splash.classList.add("is-hidden");
    liveHud.hidden = false;
    if (reticleLayer) reticleLayer.hidden = false;
    if (altitudeTapeColumn) altitudeTapeColumn.hidden = false;
    if (relativeTapeRow) relativeTapeRow.hidden = !showHudChrome;
    if (relativeZeroHud) relativeZeroHud.hidden = !showHudChrome;
    resetTiltTapeHud();
    resetCompassTapeUnwrap();
    resetAltitudeHud();
    crtOverlay.hidden = false;
    if (bboxOverlay) bboxOverlay.hidden = false;
    if (facePrivacyOverlay && devConfig.facePrivacy.enabled) {
      facePrivacyOverlay.hidden = false;
      facePrivacy.startFacePrivacy({ video, overlay: facePrivacyOverlay });
    }
    ensureLiveHealthUi();
    startVideoStallWatch(video);
    stopBtn.hidden = false;
    startCrtNoise();
    armLiveCrtBurstLoop();
    if (miniMapPanel) miniMapPanel.hidden = false;
    if (speedHud) speedHud.hidden = false;
    if (miniMapMount) {
      const miniMapApi = await loadMiniMapApi();
      miniMapApi.startMiniMap(miniMapMount, miniMapStatus, {
        speedValueEl: speedHudValue,
        onAltitude: (m) => applyAltitudeTapeTransform(m),
      });
    }
    if (compassPanel) compassPanel.hidden = !showHudChrome;
    if (showHudChrome) {
      requestAnimationFrame(() => {
        syncCompassTapeGeometry();
        setMiniMapHeadingSafe(lastHeading);
        applyTiltTapeTransform(lastTiltTapeBeta);
        applyAltitudeTapeTransform(lastAltitudeM);
        requestAnimationFrame(() => {
          syncCompassTapeGeometry();
          setMiniMapHeadingSafe(lastHeading);
          applyTiltTapeTransform(lastTiltTapeBeta);
          applyAltitudeTapeTransform(lastAltitudeM);
        });
      });
    }
    playLiveHudIntroText();
    if (compassHint) {
      if (!showHudChrome) {
        compassHint.hidden = true;
        compassHint.textContent = "";
      } else if (!deviceOrientationApiAvailable()) {
        compassHint.hidden = false;
        compassHint.textContent = uiLexicon.errors.browserOrientation;
      } else if (!orientationGranted) {
        compassHint.hidden = false;
        compassHint.textContent = uiLexicon.errors.orientationPermission;
      } else {
        compassHint.hidden = true;
        compassHint.textContent = "";
      }
    }
    if (enableMobileSensors) {
      const [compassApi, motionHudApi, motionStabilityApi] = await Promise.all([
        loadCompassApi(),
        loadMotionHudApi(),
        loadMotionStabilityApi(),
      ]);
      compassCleanup?.();
      compassCleanup = compassApi.startCompass((heading) => {
        feedCompassTapeHeading(heading);
        setMiniMapHeadingSafe(heading);
      });

      motionHudCleanup?.();
      motionHudCleanup = motionHudApi.startMotionHud((d) => {
        updateTiltTape(d);
      });

      motionStabilityCleanup?.();
      motionStabilityCleanup = motionStabilityApi.startMotionStabilityLink({
        reticleEl: reticleLayer,
        mapPanelEl: miniMapPanel,
        notifyEl: motionFeedback,
      });
    }
  } else {
    motionStabilityCleanup?.();
    motionStabilityCleanup = null;
    motionHudCleanup?.();
    motionHudCleanup = null;
    compassCleanup?.();
    compassCleanup = null;
    if (personDetectionApiPromise) {
      const personDetectionApi = await personDetectionApiPromise;
      personDetectionApi.stopPersonDetection(bboxOverlay ?? undefined);
    }
    if (miniMapApiPromise) {
      const miniMapApi = await miniMapApiPromise;
      miniMapApi.stopMiniMap(miniMapMount, miniMapStatus, {
        speedValueEl: speedHudValue,
      });
    }
    if (miniMapPanel) miniMapPanel.hidden = true;
    if (speedHud) speedHud.hidden = true;
    if (altitudeTapeColumn) altitudeTapeColumn.hidden = true;
    if (relativeTapeRow) relativeTapeRow.hidden = true;
    if (compassPanel) compassPanel.hidden = true;
    if (compassHint) {
      compassHint.hidden = true;
      compassHint.textContent = "";
    }
    splash.classList.remove("is-hidden");
    playSplashIntroText();
    liveHud.hidden = true;
    if (reticleLayer) reticleLayer.hidden = true;
    if (relativeZeroHud) relativeZeroHud.hidden = true;
    resetTiltTapeHud();
    resetAltitudeHud();
    resetCompassTapeUnwrap();
    if (bboxOverlay) bboxOverlay.hidden = true;
    facePrivacy.stopFacePrivacy(facePrivacyOverlay ?? undefined);
    facePrivacy.disposeFacePrivacyModel();
    if (facePrivacyOverlay) facePrivacyOverlay.hidden = true;
    stopBtn.hidden = true;
    if (targetFeedback) {
      clearTimeout(targetFeedbackTimer);
      targetFeedback.hidden = true;
      targetFeedback.textContent = "";
    }
    stopNoiseLoop();
    clearLiveCrtBurstTimers();
    stopVideoStallWatch();
    resetLiveHealth();
  }
}

function stopTracks() {
  stopWebXrHitTestRangeSensor();
  clearTimeout(bootHideTimer);
  stopBootTrickle();
  clearBootScrambleTimers();
  updateBootOverlay({
    visible: false,
    title: "SYSTEM BOOT",
    status: "INITIALIZING...",
    progress: 0,
  });
  if (stream) {
    for (const track of stream.getTracks()) {
      track.stop();
    }
    stream = null;
  }
  video.srcObject = null;
  video.classList.remove("active");
  startBtn.disabled = false;
  void setLive(false);
  setStatus("");
}

/**
 * @param {MediaStreamConstraints} constraints
 * @param {number} timeoutMs
 * @returns {Promise<MediaStream>}
 */
function getUserMediaWithTimeout(constraints, timeoutMs = 18000) {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(uiLexicon.errors.cameraTimeout));
    }, timeoutMs);
    navigator.mediaDevices
      .getUserMedia(constraints)
      .then((media) => {
        window.clearTimeout(timer);
        resolve(media);
      })
      .catch((err) => {
        window.clearTimeout(timer);
        reject(err);
      });
  });
}

/**
 * 外向きカメラを優先し、失敗時はフォールバックの facingMode を試す。
 * @returns {Promise<MediaStream>}
 */
async function acquireCameraStream() {
  const cam = devConfig.camera;
  const videoBase = {
    width: { ideal: cam.widthIdeal },
    height: { ideal: cam.heightIdeal },
  };
  const primary = cam.facingMode ?? "environment";
  const fallbacks = Array.isArray(cam.facingModeFallbacks)
    ? cam.facingModeFallbacks
    : [];
  const modes = [...new Set([primary, ...fallbacks])];
  /** @type {unknown} */
  let lastErr = null;
  for (const facingMode of modes) {
    try {
      return await getUserMediaWithTimeout(
        {
          video: { ...videoBase, facingMode },
          audio: false,
        },
        18000,
      );
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}

async function clampCameraZoomToMinimum(activeStream) {
  const [videoTrack] = activeStream.getVideoTracks();
  if (!videoTrack?.getCapabilities || !videoTrack.applyConstraints) return;
  try {
    const caps = videoTrack.getCapabilities();
    const zoomCap = caps?.zoom;
    if (typeof zoomCap?.min !== "number") return;
    await videoTrack.applyConstraints({
      advanced: [{ zoom: zoomCap.min }],
    });
  } catch {
    /* ズーム固定非対応端末では何もしない */
  }
}

async function startCamera() {
  if (!navigator.mediaDevices?.getUserMedia) {
    setStatus(uiLexicon.errors.browserCamera, true);
    return;
  }

  /**
   * タップ直後にカメラ取得を開始する。
   * モデル読込のあとだと iOS 等で許可が出ない／CONNECTING CAMERA で止まって見える。
   */
  const streamAcquirePromise = acquireCameraStream();

  await initAudioFx();

  startBtn.disabled = true;
  startBtn.textContent = uiLexicon.boot.preparing;
  clearTimeout(bootHideTimer);
  clearBootScrambleTimers();
  updateBootOverlay({
    visible: true,
    title: "SYSTEM BOOT",
    status: "INITIALIZING...",
    progress: 8,
  });
  updateBootStep("sensors", "pending");
  updateBootStep("model", "pending");
  updateBootStep("camera", "pending");
  startBootTrickle(18);
  /** @type {"pending" | "loading" | "ok" | "error"} */
  let sensorsStepState = "pending";
  /** @type {"pending" | "loading" | "ok" | "error"} */
  let modelStepState = "pending";
  /** @type {"pending" | "loading" | "ok" | "error"} */
  let cameraStepState = "pending";

  try {
    playBootLoadingOpenFx();

    const enableOrientationHud = orientationHudEnabled();
    updateBootOverlay({ status: "CHECKING SENSOR PERMISSION...", progress: 20 });
    playBootPhaseTickFx();
    updateBootStep("sensors", "loading");
    sensorsStepState = "loading";
    startBootTrickle(30);
    const orientationGranted = enableOrientationHud
      ? await loadCompassApi().then((api) => api.requestOrientationPermission())
      : false;
    updateBootStep("sensors", "ok");
    sensorsStepState = "ok";
    playBootStepOkFx();

    if (
      typeof DeviceMotionEvent !== "undefined" &&
      typeof DeviceMotionEvent.requestPermission === "function"
    ) {
      try {
        await DeviceMotionEvent.requestPermission();
      } catch {
        /* 未対応・拒否時は揺れ連動のみ弱くなる場合あり */
      }
    }

    setStatus(
      devConfig.facePrivacy.enabled
        ? uiLexicon.boot.loadingDual
        : uiLexicon.boot.loadingSsd,
    );
    updateBootOverlay({
      status: devConfig.facePrivacy.enabled
        ? "LOADING MODELS (SSD + BLAZE FACE)..."
        : "LOADING DETECTION MODEL...",
      progress: 44,
    });
    playBootPhaseTickFx();
    updateBootStep("model", "loading");
    modelStepState = "loading";
    startBootTrickle(52);
    const personDetectionApi = await loadPersonDetectionApi();
    const modelLoadStartedAt = performance.now();
    const modelLoads = [personDetectionApi.ensureModel()];
    if (devConfig.facePrivacy.enabled) {
      modelLoads.push(facePrivacy.ensureFacePrivacyModel());
    }
    const modelTimeoutMs =
      devConfig.liveHealth?.modelLoadTimeoutMs ?? 120000;
    await withTimeout(
      Promise.all(modelLoads),
      modelTimeoutMs,
      uiLexicon.errors.modelTimeout,
    );
    const modelLoadMs = performance.now() - modelLoadStartedAt;
    updateBootStep("model", "ok");
    modelStepState = "ok";
    playBootStepOkFx();

    setStatus(uiLexicon.boot.connecting);
    updateBootOverlay({ status: "CONNECTING CAMERA...", progress: 62 });
    playBootPhaseTickFx();
    updateBootStep("camera", "loading");
    cameraStepState = "loading";
    startBootTrickle(76);

    if (stream) {
      for (const track of stream.getTracks()) {
        track.stop();
      }
    }

    stream = await streamAcquirePromise;
    await clampCameraZoomToMinimum(stream);

    video.srcObject = stream;
    video.playsInline = true;
    try {
      await video.play();
    } catch {
      video.muted = true;
      await video.play().catch(() => {});
    }
    void tryStartWebXrHitTestRangeSensor();
    updateBootStep("camera", "ok");
    cameraStepState = "ok";
    playBootStepOkFx();
    video.classList.add("active");

    stopBootTrickle();
    updateBootOverlay({ status: "CALIBRATING HUD...", progress: 90 });

    await setLive(true, {
      orientationGranted,
      enableMobileSensors: enableOrientationHud,
    });

    if (bboxOverlay) {
      await personDetectionApi.startPersonDetection({
        video,
        overlay: bboxOverlay,
        onTargetEvent(evt) {
          const dist = formatTargetDistanceHudFragment(evt);
          if (evt.type === "lock") {
            playLockFx();
            playScrambleText(
              `LOCK ${evt.className} ${Math.round(evt.score * 100)}%${dist}`,
              {
                level: "lock",
                holdMs: 1200,
              },
            );
            return;
          }
          playDetectFx();
          if (targetFeedback) {
            clearTimeout(targetFeedbackTimer);
            targetFeedback.dataset.level = "";
            targetFeedback.hidden = false;
            const base = `TARGET ${evt.className} ${Math.round(evt.score * 100)}%`.toUpperCase();
            targetFeedback.textContent = dist ? `${base}${dist}` : base;
            targetFeedbackTimer = window.setTimeout(() => {
              targetFeedback.hidden = true;
              targetFeedback.textContent = "";
            }, 900);
          }
        },
      });
    }

    const settleMs = Math.max(180, Math.min(760, modelLoadMs * 0.22));
    await animateBootProgressTo(100, settleMs);
    updateBootOverlay({
      title: "SYSTEM READY",
      status: "LIVE FEED ONLINE",
      progress: 100,
    });
    playBootReadyFx();
    bootHideTimer = window.setTimeout(() => {
      updateBootOverlay({ visible: false });
    }, 460);
    startBtn.textContent = uiLexicon.boot.startAgain;
    setStatus("");
  } catch (err) {
    if (!stream) {
      try {
        const orphan = await streamAcquirePromise;
        for (const t of orphan.getTracks()) {
          t.stop();
        }
      } catch {
        /* 取得失敗またはタイムアウト */
      }
    }
    console.error(err);
    playErrorFx();
    if (cameraStepState !== "ok") {
      updateBootStep("camera", "error");
      cameraStepState = "error";
    }
    if (modelStepState !== "ok") {
      updateBootStep("model", "error");
      modelStepState = "error";
    }
    if (sensorsStepState !== "ok") {
      updateBootStep("sensors", "error");
      sensorsStepState = "error";
    }
    stopTracks();
    const msg =
      err instanceof Error ? err.message : uiLexicon.errors.cameraGeneric;
    setStatus(msg, true);
    startBtn.disabled = false;
    startBtn.textContent = uiLexicon.boot.startAgain;
    stopBootTrickle();
    await animateBootProgressTo(100, 220);
    updateBootOverlay({
      title: "BOOT FAILED",
      status: "RETRY AVAILABLE",
      progress: 100,
      visible: true,
    });
    bootHideTimer = window.setTimeout(() => {
      updateBootOverlay({ visible: false, title: "SYSTEM BOOT", status: "INITIALIZING...", progress: 0 });
    }, 1000);
  }
}

startBtn.addEventListener("click", () => {
  void startCamera();
});

stopBtn.addEventListener("click", () => {
  stopTracks();
});

window.addEventListener("resize", () => {
  if (stream) {
    resizeNoiseCanvas();
    if (facePrivacyOverlay && devConfig.facePrivacy.enabled) {
      facePrivacy.resizeFacePrivacyCanvas(facePrivacyOverlay);
    }
    if (miniMapApiPromise) {
      void miniMapApiPromise.then((api) => api.resizeMiniMap());
    }
    syncCompassTapeGeometry();
    setMiniMapHeadingSafe(lastHeading);
    applyTiltTapeTransform(lastTiltTapeBeta);
    applyAltitudeTapeTransform(lastAltitudeM);
  }
});

window.addEventListener("beforeunload", () => {
  stopTracks();
});

const SPLASH_BOOT_SEQUENCE_MS = 1880;
/** スクランブル完了まで含めた BIOS 表示時間 */
const BIOS_VISIBLE_MS = 3200;
const BIOS_FADE_MS = 260;

function runSplashBootSequence() {
  if (!splash) {
    playSplashIntroText();
    return;
  }
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduceMotion) {
    splash.classList.remove("splash--seq-pending");
    splash.classList.add("splash--seq-done");
    playSplashIntroText();
    return;
  }
  requestAnimationFrame(() => {
    splash.classList.add("splash--seq-run");
    playSplashIntroText({ alignWithSeqLayer: true });
    window.setTimeout(() => {
      splash.classList.remove("splash--seq-pending", "splash--seq-run");
      splash.classList.add("splash--seq-done");
    }, SPLASH_BOOT_SEQUENCE_MS);
  });
}

function playBiosSequence() {
  clearBiosScrambleTimers();
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const $ = /** @param {string} id */ (id) => document.getElementById(id);
  const rowMs = 108;

  scrambleBiosText($("biosElTitle"), "PROTO RECON ACPI BIOS v0.9 · SECURE BOOT", {
    delayMs: 0,
    stepMs: 17,
  });

  scrambleBiosText($("biosLnCpu"), "CPU Signature ............................... ", {
    delayMs: rowMs * 0,
    stepMs: 15,
  });
  biosStatFlip($("biosStCpu"), "PEND", "OK", { delayMs: rowMs * 0 + 52, gapMs: 200 });
  scrambleBiosText($("biosTlCpu"), "   PRG-A / NEON", { delayMs: rowMs * 0 + 32, stepMs: 14 });

  scrambleBiosText($("biosLnMem"), "Memory Test ................................. ", {
    delayMs: rowMs * 1,
    stepMs: 15,
  });
  biosStatFlip($("biosStMem"), "PEND", "PASS", { delayMs: rowMs * 1 + 52 });
  scrambleBiosText($("biosTlMem"), "  8192K mapped", { delayMs: rowMs * 1 + 32, stepMs: 14 });

  scrambleBiosText($("biosLnNvm"), "Local NVM ................................... ", {
    delayMs: rowMs * 2,
    stepMs: 15,
  });
  biosStatFlip($("biosStNvm"), "PEND", "ONLINE", { delayMs: rowMs * 2 + 52 });

  scrambleBiosText($("biosLnSens"), "Sensor Bus .................................. ", {
    delayMs: rowMs * 3,
    stepMs: 15,
  });
  biosStatFlip($("biosStSens"), "PEND", "ARMED", { delayMs: rowMs * 3 + 52 });

  scrambleBiosText($("biosLnDisp"), "Display Pipeline .......................... ", {
    delayMs: rowMs * 4,
    stepMs: 15,
  });
  biosStatFlip($("biosStDisp"), "INIT", "STANDBY", { delayMs: rowMs * 4 + 52, gapMs: 240 });

  scrambleBiosText(
    $("biosLnBoot"),
    "Boot Device ................................. WEB.RUNTIME · WASM-HOST",
    { delayMs: rowMs * 5 + 28, stepMs: 13 },
  );

  scrambleBiosText($("biosLnLoad"), "Loading hosted execution environment .......... ", {
    delayMs: rowMs * 6 + 18,
    stepMs: 14,
  });
  biosStatFlip($("biosStLoad"), "HOLD", "DONE", { delayMs: rowMs * 6 + 58 });

  scrambleBiosText($("biosLnShell"), "Starting UI shell", {
    delayMs: rowMs * 7 + 8,
    stepMs: 15,
  });

  scrambleBiosText($("biosElHint"), "© EXPERIMENTAL — POWERED-OFF STATE INVALID", {
    delayMs: rowMs * 7 + 240,
    stepMs: 13,
  });
}

function dismissBiosAndRunSplash() {
  clearBiosScrambleTimers();
  if (!biosOverlay) {
    runSplashBootSequence();
    return;
  }
  biosOverlay.classList.add("bios-overlay--out");
  window.setTimeout(() => {
    biosOverlay.hidden = true;
    biosOverlay.setAttribute("aria-hidden", "true");
    biosOverlay.classList.remove("bios-overlay--out");
    runSplashBootSequence();
  }, BIOS_FADE_MS);
}

function runBiosThenSplash() {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduceMotion || !biosOverlay) {
    if (biosOverlay) {
      biosOverlay.hidden = true;
      biosOverlay.setAttribute("aria-hidden", "true");
    }
    runSplashBootSequence();
    return;
  }
  playBiosSequence();
  window.setTimeout(dismissBiosAndRunSplash, BIOS_VISIBLE_MS);
}

runBiosThenSplash();
